import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Debounced scroll handler for better performance
  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY;
    setIsScrolled(scrollTop > 50);
  }, []);

  useEffect(() => {
    let timeoutId: number;
    
    const debouncedHandleScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = window.setTimeout(handleScroll, 10);
    };

    window.addEventListener('scroll', debouncedHandleScroll);
    return () => {
      window.removeEventListener('scroll', debouncedHandleScroll);
      clearTimeout(timeoutId);
    };
  }, [handleScroll]);

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/89f2e3be-f2ad-453e-92a0-c2253a51973a.png" 
                alt="Valve Sales Supply" 
                className="h-10 w-auto"
              />
              <div className="flex flex-col">
                <span className={`font-bold text-xl leading-tight transition-colors duration-300 ${
                  isScrolled ? 'text-[#1B3A5F]' : 'text-white'
                }`}>
                  Valve Sales Supply
                </span>
                <span className={`text-xs font-medium transition-colors duration-300 ${
                  isScrolled ? 'text-gray-600' : 'text-white/80'
                }`}>
                  Industrial Equipment Buyers
                </span>
              </div>
            </Link>
            
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              <Link 
                to="/" 
                className={`font-medium transition-colors duration-300 ${
                  isScrolled ? 'text-gray-700 hover:text-[#1B3A5F]' : 'text-white hover:text-white/80'
                }`}
              >
                Home
              </Link>
              <Link 
                to="/what-we-buy" 
                className={`font-medium transition-colors duration-300 ${
                  isScrolled ? 'text-gray-700 hover:text-[#1B3A5F]' : 'text-white hover:text-white/80'
                }`}
              >
                What We Buy
              </Link>
              <Link 
                to="/we-buy-surplus-valves" 
                className={`font-medium transition-colors duration-300 ${
                  isScrolled ? 'text-gray-700 hover:text-[#1B3A5F]' : 'text-white hover:text-white/80'
                }`}
              >
                We Buy Surplus Valves
              </Link>
              <Link 
                to="/how-it-works" 
                className={`font-medium transition-colors duration-300 ${
                  isScrolled ? 'text-gray-700 hover:text-[#1B3A5F]' : 'text-white hover:text-white/80'
                }`}
              >
                How It Works
              </Link>
              <Link 
                to="/service-areas" 
                className={`font-medium transition-colors duration-300 ${
                  isScrolled ? 'text-gray-700 hover:text-[#1B3A5F]' : 'text-white hover:text-white/80'
                }`}
              >
                Service Areas
              </Link>
              <Link 
                to="/blog" 
                className={`font-medium transition-colors duration-300 ${
                  isScrolled ? 'text-gray-700 hover:text-[#1B3A5F]' : 'text-white hover:text-white/80'
                }`}
              >
                Blog
              </Link>
              <Link 
                to="/about" 
                className={`font-medium transition-colors duration-300 ${
                  isScrolled ? 'text-gray-700 hover:text-[#1B3A5F]' : 'text-white hover:text-white/80'
                }`}
              >
                About
              </Link>
              <a
                href="https://www.facebook.com/valvesalessupply"
                target="_blank"
                rel="noopener noreferrer"
                className={`w-10 h-10 flex items-center justify-center transition-colors duration-300 ${
                  isScrolled ? 'text-gray-700 hover:text-[#1B3A5F]' : 'text-white hover:text-white/80'
                }`}
                aria-label="Follow us on Facebook"
              >
                <i className="ri-facebook-line text-xl"></i>
              </a>
              <Link 
                to="/contact" 
                className="bg-[#1B3A5F] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#2B4A6F] transition-colors duration-300 whitespace-nowrap"
              >
                Contact
              </Link>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className={`lg:hidden p-2 cursor-pointer flex items-center justify-center w-10 h-10 transition-colors duration-300 ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div 
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={closeMobileMenu}
          ></div>
          <div className="fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-xl transform transition-transform duration-300 ease-in-out">
            <div className="pt-20 pb-6 px-6">
              <nav className="space-y-1">
                <Link
                  to="/"
                  className="text-gray-700 hover:text-[#1B3A5F] hover:bg-gray-50 block px-4 py-3 rounded-lg font-medium transition-colors duration-300"
                  onClick={closeMobileMenu}
                >
                  Home
                </Link>
                <Link
                  to="/what-we-buy"
                  className="text-gray-700 hover:text-[#1B3A5F] hover:bg-gray-50 block px-4 py-3 rounded-lg font-medium transition-colors duration-300"
                  onClick={closeMobileMenu}
                >
                  What We Buy
                </Link>
                <Link
                  to="/we-buy-surplus-valves"
                  className="text-gray-700 hover:text-[#1B3A5F] hover:bg-gray-50 block px-4 py-3 rounded-lg font-medium transition-colors duration-300"
                  onClick={closeMobileMenu}
                >
                  We Buy Surplus Valves
                </Link>
                <Link
                  to="/how-it-works"
                  className="text-gray-700 hover:text-[#1B3A5F] hover:bg-gray-50 block px-4 py-3 rounded-lg font-medium transition-colors duration-300"
                  onClick={closeMobileMenu}
                >
                  How It Works
                </Link>
                <Link
                  to="/service-areas"
                  className="text-gray-700 hover:text-[#1B3A5F] hover:bg-gray-50 block px-4 py-3 rounded-lg font-medium transition-colors duration-300"
                  onClick={closeMobileMenu}
                >
                  Service Areas
                </Link>
                <Link
                  to="/blog"
                  className="text-gray-700 hover:text-[#1B3A5F] hover:bg-gray-50 block px-4 py-3 rounded-lg font-medium transition-colors duration-300"
                  onClick={closeMobileMenu}
                >
                  Blog
                </Link>
                <Link
                  to="/about"
                  className="text-gray-700 hover:text-[#1B3A5F] hover:bg-gray-50 block px-4 py-3 rounded-lg font-medium transition-colors duration-300"
                  onClick={closeMobileMenu}
                >
                  About
                </Link>
                <Link
                  to="/contact"
                  className="text-gray-700 hover:text-[#1B3A5F] hover:bg-gray-50 block px-4 py-3 rounded-lg font-medium transition-colors duration-300"
                  onClick={closeMobileMenu}
                >
                  Contact
                </Link>
              </nav>

              <div className="mt-8 pt-6 border-t border-gray-200">
                <a
                  href="tel:214-854-5638"
                  className="block w-full bg-[#1B3A5F] text-white text-center px-6 py-3 rounded-lg font-semibold hover:bg-[#2B4A6F] transition-colors duration-300 whitespace-nowrap"
                  onClick={closeMobileMenu}
                >
                  Call Now: (214) 854-5638
                </a>
              </div>

              <div className="mt-6 text-center">
                <a
                  href="https://www.facebook.com/valvesalessupply"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center w-10 h-10 bg-[#1B3A5F] text-white rounded-lg hover:bg-[#2B4A6F] transition-colors duration-300"
                  onClick={closeMobileMenu}
                  aria-label="Follow us on Facebook"
                >
                  <i className="ri-facebook-line text-xl"></i>
                </a>
                <p className="text-gray-600 text-sm mt-3">Industrial Equipment Buyers</p>
                <p className="text-gray-600 text-sm mt-1">Mon-Fri: 7AM-6PM CST</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;